import { gql } from '@apollo/client'

// ==================== FRAGMENTS ====================

const POLICY_ACTION_FRAGMENT = gql`
  fragment PolicyActionFields on PolicyAction {
    id
    action
    value
  }
`

const POLICY_FRAGMENT = gql`
  fragment PolicyFields on Policy {
    id
    restriction
    createdAt
    actions {
      ...PolicyActionFields
    }
  }
  ${POLICY_ACTION_FRAGMENT}
`

const POLICY_GROUP_FRAGMENT = gql`
  fragment PolicyGroupFields on PolicyGroup {
    id
    name
    description
    type
    isPrivate
    isDefault
    createdAt
  }
`

// ==================== QUERIES ====================

export const POLICY_DEFINITIONS = gql`
  query PolicyDefinitions($type: PolicyGroupType) {
    policyDefinitions(type: $type) {
      id
      name
      displayName
      valueType
      unit
      availableFor
      availableActions
      defaultValue
      active
    }
  }
`

export const POLICY_GROUPS = gql`
  query PolicyGroups($type: PolicyGroupType!) {
    policyGroups(type: $type) {
      ...PolicyGroupFields
      caregivers {
        id
      }
      patients {
        id
      }
      policies {
        ...PolicyFields
      }
    }
  }
  ${POLICY_GROUP_FRAGMENT}
  ${POLICY_FRAGMENT}
`

export const POLICY_GROUP = gql`
  query PolicyGroup($id: ID!) {
    policyGroup(id: $id) {
      ...PolicyGroupFields
      caregivers {
        id
        sfContactId
        sfContact {
          firstName
          lastName
        }
      }
      patients {
        id
        name
      }
      policies {
        ...PolicyFields
      }
    }
  }
  ${POLICY_GROUP_FRAGMENT}
  ${POLICY_FRAGMENT}
`

// ==================== POLICY GROUP MUTATIONS ====================

export const CREATE_POLICY_GROUP = gql`
  mutation CreatePolicyGroup($data: CreatePolicyGroupInput!) {
    createPolicyGroup(data: $data) {
      ...PolicyGroupFields
      policies {
        ...PolicyFields
      }
    }
  }
  ${POLICY_GROUP_FRAGMENT}
  ${POLICY_FRAGMENT}
`

export const UPDATE_POLICY_GROUP = gql`
  mutation UpdatePolicyGroup($data: UpdatePolicyGroupInput!) {
    updatePolicyGroup(data: $data) {
      ...PolicyGroupFields
    }
  }
  ${POLICY_GROUP_FRAGMENT}
`

export const DELETE_POLICY_GROUP = gql`
  mutation DeletePolicyGroup($id: ID!) {
    deletePolicyGroup(id: $id) {
      id
    }
  }
`

export const SET_DEFAULT_POLICY_GROUP = gql`
  mutation SetDefaultPolicyGroup($id: ID!) {
    setDefaultPolicyGroup(id: $id) {
      ...PolicyGroupFields
    }
  }
  ${POLICY_GROUP_FRAGMENT}
`

// ==================== ASSIGNMENT MUTATIONS ====================

export const ADD_CAREGIVER_TO_POLICY_GROUP = gql`
  mutation AddCaregiverToPolicyGroup($policyGroupId: ID!, $caregiverId: ID!) {
    addCaregiverToPolicyGroup(policyGroupId: $policyGroupId, caregiverId: $caregiverId) {
      id
      sfContactId
      sfContact {
        firstName
        lastName
      }
    }
  }
`

export const REMOVE_CAREGIVER_FROM_POLICY_GROUP = gql`
  mutation RemoveCaregiverFromPolicyGroup($policyGroupId: ID!, $caregiverId: ID!) {
    removeCaregiverFromPolicyGroup(policyGroupId: $policyGroupId, caregiverId: $caregiverId) {
      id
      sfContactId
      sfContact {
        firstName
        lastName
      }
    }
  }
`

export const ADD_PATIENT_TO_POLICY_GROUP = gql`
  mutation AddPatientToPolicyGroup($policyGroupId: ID!, $patientId: ID!) {
    addPatientToPolicyGroup(policyGroupId: $policyGroupId, patientId: $patientId) {
      id
      name
    }
  }
`

export const REMOVE_PATIENT_FROM_POLICY_GROUP = gql`
  mutation RemovePatientFromPolicyGroup($policyGroupId: ID!, $patientId: ID!) {
    removePatientFromPolicyGroup(policyGroupId: $policyGroupId, patientId: $patientId) {
      id
      name
    }
  }
`

// ==================== POLICY MUTATIONS ====================

export const CREATE_POLICY = gql`
  mutation CreatePolicy($data: CreatePolicyInput!) {
    createPolicy(data: $data) {
      ...PolicyFields
      policyGroup {
        id
      }
    }
  }
  ${POLICY_FRAGMENT}
`

export const UPDATE_POLICY = gql`
  mutation UpdatePolicy($data: UpdatePolicyInput!) {
    updatePolicy(data: $data) {
      ...PolicyFields
    }
  }
  ${POLICY_FRAGMENT}
`

export const DELETE_POLICY = gql`
  mutation DeletePolicy($id: ID!) {
    deletePolicy(id: $id) {
      id
    }
  }
`
